import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { INSET_MAX, INSET_MIN, SIDES_MAX, SIDES_MIN } from './constants/sides-limits'
import { SIDES_ELEMENT_KIND_ID } from './constants/sides-element-kind-id'
import { innerRatioForShapeType } from './helpers/inner-ratio'

/**
 * Strict whole-number parse for the Sides input: rejects trailing garbage (`parseInt` would
 * silently accept "5abc" as 5) and rounds fractional input to the nearest whole number rather
 * than truncating it (`parseInt("5.9", 10)` would silently truncate to 5).
 */
export function parseSidesCount(raw: string): { ok: true; value: number } | { ok: false } {
  const t = raw.trim()

  if (t === '') return { ok: false }
  if (!/^-?\d+(\.\d+)?$/u.test(t)) return { ok: false }
  const n = Number(t)

  if (!Number.isFinite(n)) return { ok: false }
  return { ok: true, value: Math.round(n) }
}

const SIDES_TYPES: ReadonlySet<ShapeType> = new Set([
  'square',
  'triangle',
  'pentagon',
  'hexagon',
  'star',
  'starone',
  'startwo',
])

export function isSidesEligibleShapeType(t: ShapeType): boolean {
  return SIDES_TYPES.has(t)
}

export function isSidesEligibleShape(s: { type: string }): boolean {
  return isSidesEligibleShapeType(s.type as ShapeType)
}

export function defaultSidesForType(type: ShapeType): number {
  switch (type) {
    case 'triangle':
      return 3
    case 'pentagon':
      return 5
    case 'hexagon':
      return 6
    case 'star':
    case 'starone':
    case 'startwo':
      return 5
    default:
      return 4
  }
}

/**
 * Star / burst variants (`regularStarCommandsWithTips`) use alternating outer/inner vertices:
 * one `moveTo` + `lineTo`s ⇒ `2 * tips` commands, all straight segments.
 */
function inferTipsFromStarLinePath(pathCommands: PathCommand[] | undefined | null): number | null {
  if (!pathCommands || pathCommands.length < 4) return null
  if (pathCommands.length % 2 !== 0) return null

  for (const c of pathCommands) {
    if ('curveTo' in c) return null
  }

  const tips = pathCommands.length / 2

  if (tips < SIDES_MIN || tips > SIDES_MAX) return null
  return tips
}

function isStarBurstShapeType(t: ShapeType): boolean {
  return t === 'star' || t === 'starone' || t === 'startwo'
}

export function normSides(s: ShapeData): number {
  const d = defaultSidesForType(s.type)

  if (isStarBurstShapeType(s.type)) {
    const fromPath = inferTipsFromStarLinePath(s.pathCommands)

    if (fromPath != null) {
      return fromPath
    }
  }

  const raw = s.sides

  if (typeof raw !== 'number' || !Number.isFinite(raw)) return d
  return Math.max(SIDES_MIN, Math.min(SIDES_MAX, Math.round(raw)))
}

export function defaultInsetForType(type: ShapeType): number {
  const legacyInner = innerRatioForShapeType(type)

  if (legacyInner <= 0) return 0
  return Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round((1 - legacyInner) * 100)))
}

export function normInset(s: ShapeData): number {
  const d = defaultInsetForType(s.type)
  const raw = s.inset

  if (typeof raw !== 'number' || !Number.isFinite(raw)) return d
  return Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round(raw)))
}

/**
 * True when Sides/Inset has already rewritten this shape as a `RegularPolygonElement`.
 *
 * This is the flag that disambiguates a `square`-type shape sitting at 4 sides / no inset: an
 * untouched one is a plain axis-aligned rectangle (`resolveKindId` → kindId 1), while a converted
 * one is a diamond (the 4-gon inscribed in its bounds). Both report `normSides === 4`, so the kind
 * is the only thing that tells them apart.
 */
export function isConvertedToRegularPolygon(shape: Pick<ShapeData, 'kindId'>): boolean {
  return shape.kindId === SIDES_ELEMENT_KIND_ID
}

/**
 * True when applying `nextSides` leaves the shape as (or turns it into) a `RegularPolygonElement`.
 *
 * Only a `square` still at its rectangle defaults answers false: setting a fresh rectangle's side
 * count to the 4 it already has must leave it a rectangle, not convert it to a diamond. Every
 * other shape type defaults to kindId 10 already (`resolveKindId`), and any other sides/inset
 * value is a real polygon.
 */
export function sidesChangeConvertsToRegularPolygon(shape: ShapeData, nextSides: number): boolean {
  if (isConvertedToRegularPolygon(shape)) return true
  if (shape.type !== 'square') return true

  return nextSides !== 4 || normInset(shape) > 0
}

/**
 * True when applying `nextSides` would leave the document untouched, so the caller must not write
 * to the shape at all — no kind restamp, no path rewrite, no history entry.
 *
 * Covers the two ways a side-count edit can be a no-op:
 *  - a rectangle/square asked for the 4 sides it already has, which stays a plain rectangle;
 *  - an already-converted polygon asked for the count it already has.
 */
export function isNoOpSidesChange(shape: ShapeData, nextSides: number): boolean {
  if (normSides(shape) !== nextSides) return false
  if (!sidesChangeConvertsToRegularPolygon(shape, nextSides)) return true

  return isConvertedToRegularPolygon(shape)
}

/**
 * True when the shape already is — or is always saved as — a `RegularPolygonElement`.
 *
 * A `square`-type shape is the one type that starts life as something else: a plain
 * `RectangleElement` (`resolveKindId` → kindId 1) until Sides converts it. Every other
 * sides-eligible type saves as a regular polygon from creation, so it qualifies even before its
 * `kindId` has been stamped (freshly drawn shapes carry no `kindId` yet).
 */
export function isRegularPolygonShape(shape: ShapeData): boolean {
  if (isConvertedToRegularPolygon(shape)) return true
  if (!isSidesEligibleShape(shape)) return false

  return shape.type !== 'square'
}

/**
 * True when Inset may be applied to this shape.
 *
 * Stricter than {@link isSidesEligibleShape}: Inset must never be the operation that turns a
 * rectangle into a diamond. A plain rectangle/square has no inner radius to inset — the only way
 * to give it one is a 4-gon, and reaching that is Sides' job. So Inset stays disabled until the
 * shape is genuinely a regular polygon, at which point insetting its vertices is meaningful.
 */
export function isInsetEligibleShape(shape: ShapeData): boolean {
  return isSidesEligibleShape(shape) && isRegularPolygonShape(shape)
}

/**
 * True when applying `nextInset` would leave the document untouched, so the caller must not write
 * to the shape at all — no kind restamp, no path rewrite, no history entry.
 *
 * Covers both ways an inset edit is a no-op:
 *  - the shape cannot take an inset at all, which includes a rectangle that Sides has not yet
 *    converted (defence behind the disabled control — Inset must never do that conversion itself);
 *  - the shape already sits at the requested inset.
 */
export function isNoOpInsetChange(shape: ShapeData, nextInset: number): boolean {
  if (!isInsetEligibleShape(shape)) return true

  return normInset(shape) === nextInset
}
